import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Tree, message } from 'antd';
import { useDispatch } from 'react-redux';
import { localize } from 'locales';
import { updateUserProfile } from '../../../../redux/user';
import addIcon from '../../../../assets/images/icon-add.svg';
import editIcon from '../../../../assets/images/icon-edit.svg';
import * as api from '../../../../api';
import './index.css';
const RoleEdit = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { roleId } = useParams();
	const [form] = Form.useForm();

	const [checkedKeys, setCheckedKeys] = useState([]);
	const [permissions, setPermissions] = useState([]);

	let currentRole = useRef(null);
	let permissionTree = useRef([]);

	useEffect(() => {
		getPermissionData();
	}, []);

	useEffect(() => {
		if (roleId) {
			getRoleData(roleId);
		}
	}, [roleId]);
	// MARK: - helper functions
	function _treeDataProvider(rawData, parentKey = '0') {
		const treeData = [];
		for (let i = 0; i < rawData.length; i++) {
			const nodeData = rawData[i];
			const isLeaf = nodeData.children == null || nodeData.children.length === 0;
			const nodeKey = parentKey + '-' + i;
			const node = {
				title: nodeData.name,
				key: nodeKey,
				isLeaf: isLeaf,
				value: nodeData.code,
			};
			if (!isLeaf) {
				const children = _treeDataProvider(nodeData.children, nodeKey);
				node.children = children;
			}
			treeData.push(node);
		}
		return treeData;
	}

	function _getSelectedPermissionCode(keyIndexes, rawData) {
		const indexes = [...keyIndexes];
		const currentIndex = indexes.pop();
		const currentNode = rawData[currentIndex];
		if (indexes.length > 0 && currentNode.children) {
			return _getSelectedPermissionCode(indexes, currentNode.children);
		} else {
			return currentNode.value;
		}
	}
	function _getCheckedPermissions() {
		const checkedPermissions = [];
		(checkedKeys ?? []).forEach((key) => {
			const nodeIndexes = key.split('-').reverse();
			// for the first level of the tree, node key will be like "0-0", node indexes will be like [0, 0]
			// so we need to remove the first index
			nodeIndexes.pop();
			const permissionCode = _getSelectedPermissionCode(nodeIndexes, permissionTree.current);
			checkedPermissions.push(permissionCode);
		});

		return checkedPermissions.filter((permissionCode) => permissionCode != null);
	}

	function _recursivelyReadTree(parentNodes) {
		const leafs = [];
		parentNodes.forEach((node) => {
			if (!node.children) {
				leafs.push(node);
			} else {
				leafs.push(..._recursivelyReadTree(node.children));
			}
		});
		return leafs;
	}

	// MARK: - api calls
	async function getPermissionData() {
		api.getPermissions().then((res) => {
			const permissionData = _treeDataProvider(res);
			permissionTree.current = permissionData;
			setPermissions(permissionData);
			_updateRoleStatus();
		});
	}

	async function getRoleData() {
		api.getRole(roleId).then((role) => {
			currentRole.current = role;
			form.setFieldsValue({
				role: role.role,
				description: role.description,
			});
			_updateRoleStatus();
		});
	}

	function _updateRoleStatus() {
		if (currentRole.current && permissionTree.current && permissionTree.current.length > 0) {
			const rolePermissions = currentRole.current.permissions;
			// setCheckedKeys(['0-0-0', '0-0-1', '0-0-2', '0-0-3', '0-0-4', '0-0-5', '0-0-6', '0-0-7', '0-0-8', '0-0-9']);
			const permissionLeafs = _recursivelyReadTree([...permissionTree.current]);
			const selectedPermission = permissionLeafs.filter((node) => rolePermissions.includes(node.value));
			setCheckedKeys(selectedPermission.map((permission) => permission.key));
		}
	}
	function save() {
		form.validateFields().then((values) => {
			const payload = {
				...values,
				permissions: _getCheckedPermissions(),
			};

			if (!roleId) {
				api.createRole(payload).then(() => {
					navigate('/permission/role');
					message.success('Role created successfully!');
				});
			} else {
				api.updateRole(roleId, payload).then(() => {
					navigate('/permission/role');
					dispatch(updateUserProfile());
					message.success('Role saved successfully!');
				});
			}
		});
	}
	function onCheck(checkedKeys) {
		setCheckedKeys(checkedKeys);
	}

	return (
		<div className="app-page-container role-view">
			<div className="app-navbar">
				<div className="app-navbar-text page-title">
					<img src={roleId ? editIcon : addIcon} />
					{roleId ? 'Edit Role' : 'Add Role'}
				</div>
				<div className="page-actions">
					<div
						className="app-btn app-btn-edit app-btn-padding app-btn-min-width"
						onClick={() => navigate('/permission/role')}
					>
						{localize('cancel')}
					</div>
					<div className="app-btn app-btn-primary app-btn-min-width" onClick={save}>
						{localize('save')}
					</div>
				</div>
			</div>

			<div className="page-content">
				<div className="app-section">
					<div className="app-section-header">Role Details</div>
					<Form
						name="role_form"
						form={form}
						layout="vertical"
						initialValues={{
							role: '',
							description: '',
						}}
						autoComplete="off"
					>
						<Form.Item
							name="role"
							label={'Role Name'}
							validateTrigger="onBlur"
							rules={[
								{
									required: true,
									message: localize('inputRoleName'),
								},
							]}
						>
							<Input
								className="input-con-all"
								maxLength={64}
								value={form.role}
								id="role"
								placeholder={localize('roleName')}
							/>
						</Form.Item>
						<Form.Item
							name="description"
							label={'Role Description'}
							validateTrigger="onBlur"
							rules={[
								{
									required: false,
									message: localize('inputDescription'),
								},
							]}
						>
							<Input
								className="input-con-all"
								maxLength={64}
								id="description"
								value={form.description}
								placeholder={localize('description')}
							/>
						</Form.Item>
					</Form>
				</div>

				<div className="app-section">
					<div className="app-section-header">Role Permissions</div>
					<div className="tree-view-container">
						<Tree checkable onCheck={onCheck} checkedKeys={checkedKeys} treeData={permissions} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default RoleEdit;
